import {
  nativeFruitHasRenderableHalves,
  type NativeComboPayload,
  type NativeFruitPayload,
  type NativeGameFrame,
} from 'shared/lib/tv-game/nativeFrame.ts';
import {
  computeRemoteViewLayout,
  type RemoteViewLayout,
} from 'shared/lib/tv-game/nativeLayout.ts';
import type { KeypointType, PoseKeypoint } from 'shared/lib/tv-game/pose.ts';
import { wristsAsHands, WRIST_MIN_CONFIDENCE } from 'shared/lib/tv-game/pose.ts';
import { applyNormFlip, toUnitScalarPose } from 'shared/lib/tv-game/normCoord.ts';
import { OneEuroPoint } from 'shared/lib/tv-game/oneEuro.ts';
import { getRemoteCalibStartFruitMetrics } from 'shared/lib/tv-game/remoteCalibStandZone.ts';

import {
  updateParticle, isAlive, spawnExplosion, spawnJuice,
} from '../domain/Particle.ts';
import { JuiceParticlePool } from '../domain/ParticlePool.ts';
import {
  createRemoteInitialState, type RemoteDisplayState, type RemoteFruitRow, type CalibNormMap,
} from '../domain/RemoteDisplayState.ts';
import {
  degToRad,
  juiceColorForRemoteFruit,
  resolveRemoteFruitHalfPng,
  resolveRemoteFruitPng,
  spriteIndexFromImageKey,
} from '../media/remoteFruitSprites.ts';
import type { IGameRenderer } from '../ports/IGameRenderer.ts';
import type { BladeKey } from '../domain/GameState.ts';
import type { SoundManager } from '../infrastructure/SoundManager.ts';
import { getTvQualityConfig, type TvQualityConfig } from '../infrastructure/qualityProfile.ts';

import { FruitPositionBuffer } from './FruitPositionBuffer.ts';
import { setCalibrationAlert } from '../games/shared/state/calibrationAlertStore.ts';

const TRAIL_MAX_LENGTH = 20;
/** Require this many consecutive `isPaused: false` frames before leaving pause (anti-flicker). */
const PAUSE_CLEAR_FRAMES = 3;
/** Skeleton smoothing can be tuned separately from fruit interpolation. */
const USE_SKELETON_KEYPOINT_SMOOTHING = false;
const USE_SKELETON_TRAIL_SMOOTHING = false;

/** Bomb flash длится ~700мс — столько же держим принудительный рендер после trigger. */
const BOMB_FLASH_ANIMATION_MS = 720;

function fruitHitTrackingKey(f: NativeFruitPayload, index: number): string {
  if (f.id !== undefined && f.id !== null && `${f.id}` !== '') {
    return `id:${f.id}`;
  }
  return `idx:${index}`;
}

export class RemoteFruitDisplayEngine {
  private readonly state: RemoteDisplayState = createRemoteInitialState();

  private canvasWidth = 1920;

  private canvasHeight = 1080;

  private prevHitByFruitKey = new Map<string, boolean>();
  private nextHitMapBuf = new Map<string, boolean>();
  private readonly particlePool = new JuiceParticlePool();
  /** Pre-allocated fruit row buffer — reused each frame to avoid Array alloc. */
  private readonly rowsBuf: RemoteFruitRow[] = [];
  /** Pre-allocated liveIds buffer — reused each frame. */
  private readonly liveIdsBuf: (string | number)[] = [];
  /** Pre-allocated activeBombIds buffer — reused each frame. */
  private readonly activeBombIdsBuf: (string | number)[] = [];

  private pauseClearStreak = 0;

  private cachedAspectRatio: number | undefined = undefined;

  private cachedFruitSize: number | undefined = undefined;

  private cachedNormRefWidth: number | undefined = undefined;

  private cachedNormRefHeight: number | undefined = undefined;

  private cachedFlipX: boolean | undefined = undefined;

  private cachedFlipY: boolean | undefined = undefined;

  private onHudCallback?: (score: number, lives: number, combo: number) => void;

  onCalibrationComplete?: () => void;

  private sound?: SoundManager;

  private prevFrameIsRestart = false;

  private quality: TvQualityConfig;

  private readonly keypointFilters = new Map<KeypointType, OneEuroPoint>();
  private readonly calibNormMapBuf: CalibNormMap = new Map();
  private readonly calibNormMapP2Buf: CalibNormMap = new Map();

  private readonly trailFilters = new Map<BladeKey, OneEuroPoint>();

  private readonly fruitBuffer = new FruitPositionBuffer(80);

  /** Render gating — true когда сцена изменилась и требует перерисовки. */
  private dirty = true;

  /** Принудительный рендер до этой отметки (bomb flash, пост-spawn fade). */
  private animationUntil = 0;

  private lastTickMs = 0;

  setSoundManager(sm: SoundManager): void {
    this.sound = sm;
  }

  constructor(
    private readonly renderer: IGameRenderer,
    quality: TvQualityConfig = getTvQualityConfig('high'),
  ) {
    this.quality = quality;
  }

  setQualityConfig(q: TvQualityConfig): void {
    this.quality = q;
    this.dirty = true;
  }

  setCanvasSize(width: number, height: number): void {
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.dirty = true;
  }

  setPlayerCount(n: number): void {
    const split = n === 2;
    if (this.state.splitTwoPlayers !== split) {
      this.state.splitTwoPlayers = split;
      this.dirty = true;
    }
  }

  onHudUpdate(cb: (score: number, lives: number, combo: number) => void): void {
    this.onHudCallback = cb;
  }

  getPhase(): RemoteDisplayState['phase'] {
    return this.state.phase;
  }

  getState(): Readonly<RemoteDisplayState> {
    return this.state;
  }

  setPhase(phase: RemoteDisplayState['phase']): void {
    if (this.state.phase !== phase) this.dirty = true;
    this.state.phase = phase;
  }

  private resetTransientState(): void {
    this.state.fruits = [];
    this.state.particles = [];
    this.state.bladeTrails.clear();
    this.prevHitByFruitKey.clear();
    this.pauseClearStreak = 0;
    this.cachedAspectRatio = undefined;
    this.cachedFruitSize = undefined;
    this.cachedNormRefWidth = undefined;
    this.cachedNormRefHeight = undefined;
    this.cachedFlipX = undefined;
    this.cachedFlipY = undefined;
    this.keypointFilters.clear();
    this.trailFilters.clear();
    this.fruitBuffer.clear();
    this.lastTickMs = 0;
    this.state.calibNormPointsP2 = new Map();
    this.state.player2Score = null;
    this.state.player2Lives = null;
    this.state.player2Streak = null;
    this.state.player1GameOver = false;
    this.state.player2GameOver = false;
    this.dirty = true;
  }

  startGame(): void {
    this.state.phase = 'playing';
    this.resetTransientState();
    this.state.remoteView = null;
    this.state.gameStartTime = Date.now();
    this.sound?.reset();
    this.onHudCallback?.(0, 3, 0);
    setCalibrationAlert(null);
  }

  stopGame(): void {
    this.state.phase = 'lobby';
    this.dirty = true;
    setCalibrationAlert(null);
  }

  startCalibration(): void {
    this.state.phase = 'calibration';
    this.resetTransientState();
    this.state.calibNormPoints = new Map();
    this.state.calibSliced = false;
    this.state.gameStartTime = Date.now();
  }

  onGameFrame(playerId: string, frame: NativeGameFrame): void {
    const restart = frame.isRestart === true;
    if (restart && !this.prevFrameIsRestart) {
      this.sound?.reset();
      this.startCalibration();
      this.state.isGameOver = false;
      this.state.isPaused = false;
    }
    this.prevFrameIsRestart = restart;

    if (this.state.phase !== 'playing' && this.state.phase !== 'calibration') return;

    if ('calibration' in frame) {
      setCalibrationAlert(this.state.phase === 'calibration' ? frame.calibration ?? null : null);
    }

    const W = this.canvasWidth;
    const H = this.canvasHeight;
    if (frame.normRefWidth !== undefined) this.cachedNormRefWidth = frame.normRefWidth;
    if (frame.normRefHeight !== undefined) this.cachedNormRefHeight = frame.normRefHeight;
    if (frame.flipX !== undefined) this.cachedFlipX = frame.flipX;
    if (frame.flipY !== undefined) this.cachedFlipY = frame.flipY;
    if (frame.aspectRatio !== undefined) this.cachedAspectRatio = frame.aspectRatio;
    if (frame.fruitSize !== undefined) this.cachedFruitSize = frame.fruitSize;
    const layout = computeRemoteViewLayout(W, H, this.cachedAspectRatio, this.cachedFruitSize);
    this.state.remoteView = layout;

    const now = Date.now();
    // New format sends keypoints inside player1/player2 slices; old format uses flat keypoints.
    const hasPlayerSlices = frame.player1 !== undefined;
    if (hasPlayerSlices) {
      const kps1 = frame.player1?.keypoints ?? [];
      const kps2 = frame.player2?.keypoints ?? [];
      this.state.calibNormPoints = this.buildCalibNormMap(kps1, this.calibNormMapBuf);
      this.state.calibNormPointsP2 = this.buildCalibNormMap(kps2, this.calibNormMapP2Buf);
      this.applyKeypointsToTrails('slot1', kps1, now, layout);
      this.state.p1PlayerId = 'slot1';
      if (frame.player2 !== undefined) {
        this.applyKeypointsToTrails('slot2', kps2, now, layout);
        this.state.p2PlayerId = 'slot2';
      } else {
        this.state.p2PlayerId = null;
      }
    } else {
      this.applyKeypointsToTrails(playerId, frame.keypoints, now, layout);
      this.state.calibNormPoints = this.buildCalibNormMap(frame.keypoints, this.calibNormMapBuf);
      this.calibNormMapP2Buf.clear();
      this.state.calibNormPointsP2 = this.calibNormMapP2Buf;
      this.state.p1PlayerId = playerId;
      this.state.p2PlayerId = null;
    }

    if (this.state.phase === 'calibration') {
      if (!this.state.calibSliced) {
        this.checkCalibSlice(layout);
      }
      this.dirty = true;
      return;
    }

    let comboGameNormFromLastFruitSlice: { x: number; y: number } | undefined;
    if (frame.fruits !== undefined) {
      for (let i = 0; i < frame.fruits.length; i++) {
        const f = frame.fruits[i]!;
        const key = fruitHitTrackingKey(f, i);
        const wasHit = this.prevHitByFruitKey.get(key) ?? false;
        if (f.is_hit && !wasHit && !f.is_bomb) {
          const { nx, ny } = this.fruitNormToCanvas(f.x, f.y);
          comboGameNormFromLastFruitSlice = { x: nx, y: ny };
        }
      }
    }

    this.applyAuthoritativeHud(frame, comboGameNormFromLastFruitSlice);

    this.dirty = true;

    if (frame.fruits === undefined) return;

    // Single pass over frame.fruits — no intermediate allocations.
    const rows = this.rowsBuf;
    rows.length = 0;
    const liveIds = this.liveIdsBuf;
    liveIds.length = 0;
    const activeBombIds = this.activeBombIdsBuf;
    activeBombIds.length = 0;

    for (let i = 0; i < frame.fruits.length; i++) {
      const f = frame.fruits[i]!;

      // Collect active bomb ids for fuse sound.
      if (f.is_bomb && !f.is_hit && f.id !== undefined && !(f.x === 0 && f.y === 0)) {
        const { nx, ny } = this.fruitNormToCanvas(f.x, f.y);
        if (this.isFruitCenterInGameView(nx, ny)) {
          activeBombIds.push(f.id as number | string);
        }
      }

      if (f.x === 0 && f.y === 0 && !f.is_hit) continue;

      const { nx, ny } = this.fruitNormToCanvas(f.x, f.y);
      const spriteIndex = spriteIndexFromImageKey(f.image, i);
      const pngSrc = resolveRemoteFruitPng(f.image, f.is_bomb, i);
      const halfPngSrc = f.is_bomb ? undefined : resolveRemoteFruitHalfPng(f.image, i);
      const rotationRad = degToRad(f.rotation ?? 0);
      let nativeHalves: RemoteFruitRow['nativeHalves'];
      if (nativeFruitHasRenderableHalves(f)) {
        const a = this.fruitNormToCanvas(f.x1!, f.y1!);
        const b = this.fruitNormToCanvas(f.x2!, f.y2!);
        nativeHalves = {
          half1: { nx: a.nx, ny: a.ny, rotationRad: degToRad(f.rotation1!) },
          half2: { nx: b.nx, ny: b.ny, rotationRad: degToRad(f.rotation2!) },
        };
      }
      const row: RemoteFruitRow = {
        nx,
        ny,
        isBomb: f.is_bomb,
        isHit: f.is_hit,
        spriteIndex,
        pngSrc,
        rotationRad,
        ...(halfPngSrc !== undefined ? { halfPngSrc } : {}),
        ...(f.id !== undefined ? { fruitId: f.id } : {}),
        ...(nativeHalves !== undefined ? { nativeHalves } : {}),
      };
      rows.push(row);

      // Interpolation buffer updates.
      if (row.fruitId !== undefined) {
        if (row.isHit) {
          this.fruitBuffer.forget(row.fruitId);
        } else {
          this.fruitBuffer.push(row.fruitId, { t: now, nx: row.nx, ny: row.ny, rotationRad: row.rotationRad });
          liveIds.push(row.fruitId);
        }
      }
    }

    this.fruitBuffer.retainOnly(liveIds);
    this.sound?.tickBombFuse(activeBombIds);

    this.sound?.tickFlyOut(frame.fruits);

    const nextHitMap = this.nextHitMapBuf;
    nextHitMap.clear();

    frame.fruits.forEach((f, i) => {
      const key = fruitHitTrackingKey(f, i);
      const wasHit = this.prevHitByFruitKey.get(key) ?? false;
      nextHitMap.set(key, f.is_hit);

      if (f.is_hit && !wasHit) {
        const { nx, ny } = this.fruitNormToCanvas(f.x, f.y);
        const x = layout.offsetX + nx * layout.gameWidth;
        const y = layout.offsetY + ny * layout.gameHeight;
        const softCap = Math.floor(this.quality.maxParticles * 0.75);
        const particlesNotFull = this.state.particles.length < softCap;
        if (f.is_bomb) {
          this.renderer.triggerBombFlash(x, y);
          this.animationUntil = Math.max(this.animationUntil, now + BOMB_FLASH_ANIMATION_MS);
          this.sound?.playBombBlast();
        } else {
          this.sound?.playFruitCut();
        }
      }
    });

    // swap buffers: next becomes prev, prev becomes next (reused next frame)
    const tmp = this.prevHitByFruitKey;
    this.prevHitByFruitKey = nextHitMap;
    this.nextHitMapBuf = tmp;
    this.state.fruits = rows;
  }

  private gameNormToViewportFrac(nx: number, ny: number): { x: number; y: number } {
    const layout = this.state.remoteView;
    if (!layout) return { x: nx, y: ny };
    const W = this.canvasWidth;
    const H = this.canvasHeight;
    const px = layout.offsetX + nx * layout.gameWidth;
    const py = layout.offsetY + ny * layout.gameHeight;
    return { x: px / W, y: py / H };
  }

  private finalizeComboForHud(c: NativeComboPayload): NativeComboPayload {
    const { x, y } = this.gameNormToViewportFrac(c.x, c.y);
    return { ...c, x, y };
  }

  private applyAuthoritativeHud(
    frame: NativeGameFrame,
    comboGameNormFromLastFruitSlice?: { x: number; y: number },
  ): void {
    if (frame.gameMode !== undefined) this.state.gameMode = frame.gameMode;

    const hasPlayerSlices = frame.player1 !== undefined;
    const p1 = frame.player1 ?? null;
    const p2 = frame.player2 ?? null;

    if (hasPlayerSlices) {
      // New format: HUD data is inside player slices
      if (p1?.score !== undefined && p1.score !== null) this.state.score = p1.score;
      if (p1?.streak !== undefined && p1.streak !== null) this.state.streak = p1.streak;
      if (p1?.lives !== undefined) this.state.lives = p1.lives ?? null;

      // player2 can be null even in 2-player mode (player off-camera); only update when present.
      if (p2 !== null) {
        if (p2.score !== null && p2.score !== undefined) this.state.player2Score = p2.score;
        if (p2.lives !== undefined) this.state.player2Lives = p2.lives ?? null;
        if (p2.streak !== undefined && p2.streak !== null) this.state.player2Streak = p2.streak;
      }
    } else {
      // Old format: flat HUD fields
      if (frame.score !== undefined) this.state.score = frame.score;
      if (frame.streak !== undefined) this.state.streak = frame.streak;
      if ('lives' in frame) this.state.lives = frame.lives ?? null;
    }

    if ('bestScore' in frame) this.state.bestScore = frame.bestScore ?? null;
    if ('timeLeft' in frame) this.state.timeLeft = frame.timeLeft ?? null;

    // Combo: from player1 slice (new format) or flat field (old format)
    let comboSource: NativeComboPayload | null | undefined;
    if (hasPlayerSlices) {
      if (p1 !== null && 'combo' in p1) comboSource = p1.combo ?? null;
    } else if ('combo' in frame) {
      comboSource = frame.combo ?? null;
    }
    if (comboSource !== undefined) {
      let c = comboSource;
      if (c !== null) {
        if (comboGameNormFromLastFruitSlice) {
          c = this.finalizeComboForHud({
            ...c,
            x: comboGameNormFromLastFruitSlice.x,
            y: comboGameNormFromLastFruitSlice.y,
          });
        } else {
          c = this.finalizeComboForHud(c);
        }
      }
      this.state.combo = c;
    }

    // Player 2 combo (split mode only).
    if (hasPlayerSlices && p2 !== null && 'combo' in p2) {
      const c2Raw = p2.combo ?? null;
      this.state.combo2 = c2Raw !== null ? this.finalizeComboForHud(c2Raw) : null;
    } else if (!hasPlayerSlices) {
      this.state.combo2 = null;
    }

    if (frame.isPaused !== undefined) {
      if (frame.isPaused) {
        this.pauseClearStreak = 0;
        this.state.isPaused = true;
      } else {
        this.pauseClearStreak += 1;
        if (this.pauseClearStreak >= PAUSE_CLEAR_FRAMES) {
          this.state.isPaused = false;
        }
      }
    }
    if (frame.isGameOver !== undefined) this.state.isGameOver = frame.isGameOver;

    // In split mode: track per-player game over when lives reach 0 (lives-based modes).
    // gameMode may arrive later than lives, so we check lives presence rather than mode.
    if (this.state.splitTwoPlayers) {
      if (this.state.lives !== null && this.state.lives <= 0) this.state.player1GameOver = true;
      if (this.state.player2Lives !== null && this.state.player2Lives <= 0) this.state.player2GameOver = true;
    }
  }

  private buildCalibNormMap(keypoints: readonly PoseKeypoint[], buf: CalibNormMap): CalibNormMap {
    const tSec = Date.now() / 1000;
    buf.clear();
    for (const kp of keypoints) {
      const raw = this.deviceToCanvasNorm(kp.x, kp.y);
      if (!USE_SKELETON_KEYPOINT_SMOOTHING) {
        buf.set(kp.type, { nx: raw.nx, ny: raw.ny });
        continue;
      }
      let flt = this.keypointFilters.get(kp.type);
      if (!flt) {
        flt = new OneEuroPoint(2.4, 0.05);
        this.keypointFilters.set(kp.type, flt);
      }
      const { x: nx, y: ny } = flt.filter(raw.nx, raw.ny, tSec);
      buf.set(kp.type, { nx, ny });
    }
    return buf;
  }

  private checkCalibSlice(layout: RemoteViewLayout): void {
    const { cx, cy, r } = getRemoteCalibStartFruitMetrics(layout, 'upper');
    const now = Date.now();
    let sliced = false;
    this.state.bladeTrails.forEach((trail) => {
      if (sliced) return;
      // Find the most recent point within 250ms — scan from end, no alloc.
      let tip = null;
      for (let i = trail.length - 1; i >= 0; i--) {
        if (now - trail[i]!.t < 250) { tip = trail[i]!; break; }
      }
      if (!tip) return;
      const dx = tip.x - cx;
      const dy = tip.y - cy;
      if (dx * dx + dy * dy < r * r) {
        sliced = true;
      }
    });
    if (sliced) {
      this.state.calibSliced = true;
      this.onCalibrationComplete?.();
    }
  }

  private fruitNormToCanvas(fx: number, fy: number): { nx: number; ny: number } {
    const flipX = this.cachedFlipX !== undefined ? this.cachedFlipX : this.readUrlNormFlipX();
    const flipY = this.cachedFlipY !== undefined ? this.cachedFlipY : this.readUrlNormFlipY();
    return applyNormFlip(fx, fy, { flipX, flipY });
  }

  private isFruitCenterInGameView(nx: number, ny: number): boolean {
    const m = 0.08;
    return nx >= -m && nx <= 1 + m && ny >= -m && ny <= 1 + m;
  }

  private deviceToCanvasNorm(rawX: number, rawY: number): { nx: number; ny: number } {
    const nx0 = toUnitScalarPose(rawX, this.cachedNormRefWidth);
    const ny0 = toUnitScalarPose(rawY, this.cachedNormRefHeight);
    const flipX = this.cachedFlipX !== undefined ? this.cachedFlipX : this.readUrlNormFlipX();
    const flipY = this.cachedFlipY !== undefined ? this.cachedFlipY : this.readUrlNormFlipY();
    return applyNormFlip(nx0, ny0, { flipX, flipY });
  }

  private readUrlNormFlipX(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return new URLSearchParams(window.location.search).get('normFlipX') === '1';
    } catch {
      return false;
    }
  }

  private readUrlNormFlipY(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return new URLSearchParams(window.location.search).get('normFlipY') === '1';
    } catch {
      return false;
    }
  }

  private applyKeypointsToTrails(
    playerId: string,
    keypoints: readonly PoseKeypoint[],
    now: number,
    layout: RemoteViewLayout,
  ): void {
    const hands = wristsAsHands([...keypoints]);
    const present = new Set<BladeKey>();
    const tSec = now / 1000;

    hands.forEach(({
      hand, x, y, confidence,
    }) => {
      const key: BladeKey = `${playerId}_${hand}`;

      if (confidence !== undefined && confidence < WRIST_MIN_CONFIDENCE) {
        return;
      }
      if (x === 0 && y === 0) {
        return;
      }

      present.add(key);

      if (!this.state.bladeTrails.has(key)) {
        this.state.bladeTrails.set(key, []);
      }
      const trail = this.state.bladeTrails.get(key)!;
      const raw = this.deviceToCanvasNorm(x, y);
      let nx = raw.nx;
      let ny = raw.ny;
      if (USE_SKELETON_TRAIL_SMOOTHING) {
        let flt = this.trailFilters.get(key);
        if (!flt) {
          flt = new OneEuroPoint(4.0, 0.08);
          this.trailFilters.set(key, flt);
        }
        const filtered = flt.filter(raw.nx, raw.ny, tSec);
        nx = filtered.x;
        ny = filtered.y;
      }
      const px = layout.offsetX + nx * layout.gameWidth;
      const py = layout.offsetY + ny * layout.gameHeight;
      trail.push({ x: px, y: py, t: now });
      if (trail.length > TRAIL_MAX_LENGTH) trail.shift();
    });

    this.state.bladeTrails.forEach((trail, key) => {
      if (!key.startsWith(`${playerId}_`)) return;
      if (!present.has(key)) {
        trail.length = 0;
        this.trailFilters.get(key)?.reset();
      }
    });
  }

  /** true = сцена требует нового draw'а; false = можно пропустить. */
  private shouldRender(): boolean {
    if (!this.quality.renderOnlyWhenDirty) return true;
    if (this.dirty) return true;
    if (this.state.phase === 'calibration' && this.quality.calibPulse) return true;
    if (this.state.particles.length > 0) return true;
    if (this.state.fruits.length > 0) return true;
    if (Date.now() < this.animationUntil) return true;
    return false;
  }

  tick(): void {
    const now = Date.now();
    const dt = this.lastTickMs > 0 ? Math.min(100, now - this.lastTickMs) : 16.6;
    this.lastTickMs = now;

    if (this.state.phase === 'playing') {
      const ps = this.state.particles;
      let write = 0;
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i]!;
        updateParticle(p, dt);
        if (isAlive(p)) {
          if (write !== i) ps[write] = p;
          write++;
        } else if (p.kind === 'juice') {
          this.particlePool.release(p);
        }
      }
      ps.length = Math.min(write, this.quality.maxParticles);

      // Interpolate fruit positions from buffer (in-place mutation, no realloc).
      for (const row of this.state.fruits) {
        if (row.fruitId === undefined || row.isHit) continue;
        const snap = this.fruitBuffer.sampleAt(row.fruitId, now);
        if (snap) {
          row.nx = snap.nx;
          row.ny = snap.ny;
          row.rotationRad = snap.rotationRad;
        }
      }
      this.fruitBuffer.cleanup(now);
    }

    if (this.shouldRender()) {
      this.renderer.renderRemote(this.state);
      this.dirty = false;
    }
  }

  private spawnJuicePooled(x: number, y: number, color: string, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 4;
      this.state.particles.push(
        this.particlePool.acquire(
          x, y,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed - 2,
          3 + Math.random() * 5,
          color,
        ),
      );
    }
  }

  private spawnExplosionPooled(x: number, y: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 3 + Math.random() * 6;
      this.state.particles.push(
        this.particlePool.acquire(
          x, y,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed - 3,
          4 + Math.random() * 6,
          i % 2 === 0 ? '#ff6600' : '#ffcc00',
        ),
      );
    }
  }
}
